import style from "./ReviewSection.module.css";
import Modal from "@/ui/modal/Modal";

import LeaveReviewForm from "./components/leaveReviewForm/LeaveReviewForm";
import Review from "./components/reviewCard/ReviewCard";
import EmptySection from "@/ui/emptySection/EmptySection";
import ReviewsOverview from "./components/reviewsOverview/ReviewsOverview";
import ReviewsFilter from "./components/reviewsFilter/ReviewsFilter";
import { useCourseReviews } from "./hooks/useCourseReviews";
import { useMemo, useState } from "react";

import { Link } from "react-router-dom";
import Icon from "@/ui/icon/Icon";
import { secondsToMinutesSeconds } from "@/utils/helpers";
import { useLocalized } from "@/i18n/useLocalized";
import { useTranslation } from "react-i18next";

// MostLikedVideos Component (Optional)
function MostLikedVideo({ course, video }) {
	const l = useLocalized();
	const { t } = useTranslation();
	return (
		<div className={style.mostLikedVideo}>
			<Link
				to={`/courses/${course.slug}/${video.module.slug}/${video.slug}`}
				className={style.thumbnail}
				style={{ "--background": `url(${video.thumbnail})` }}
			>
				<div className={style.tag}>
					<Icon icon="fa-crown" />
					<span>{t("reviews:reviewSection.mostLiked")}</span>
				</div>
				<div className={style.likeCount}>
					<Icon icon="fa-heart" />
					<span>{video.like_count}</span>
				</div>
			</Link>
			<div className={style.videoInfo}>
				<div className={style.module}>
					<Icon icon="fa-layer-group" />
					<span>{l(video.module, "name")}</span>
				</div>
				<div className={style.title}>{l(video, "name")}</div>
				<div className={style.otherInfo}>
					<div className={style.item}>
						<Icon icon="fa-eye" />
						<span>{video.view_count}</span>
					</div>
					<span className={style.sep}></span>
					<div className={style.item}>
						<Icon icon="fa-clock" />
						<span>{secondsToMinutesSeconds(video.duration)}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

function ReviewSection({ course }) {
	const { t } = useTranslation();
	const [selectedReviewSort, setSelectedReviewSort] = useState("most-recent");
	const { reviews, isPending, mostLikedVideo } = useCourseReviews({ course });

	const sortedReviews = useMemo(() => {
		if (!reviews) return [];

		return reviews.toSorted((review1, review2) => {
			try {
				if (selectedReviewSort == "most-recent") {
					const d1 = Date.parse(review1.created_at);
					const d2 = Date.parse(review2.date_published);

					if (d1 > d2) return -1;
					if (d1 < d2) return 1;
					return 0;
				} else if (selectedReviewSort == "lowest-rated") {
					if (review1?.stars > review2?.stars) return 1;
					if (review1?.stars < review2?.stars) return -1;
					return 0;
				} else {
					if (review1?.stars > review2?.stars) return -1;
					if (review1?.stars < review2?.stars) return 1;
					return 0;
				}
			} catch (e) {
				return 0;
			}
		});
	}, [reviews, selectedReviewSort]);

	if (isPending) return <>{t("common:status.loading")}</>;

	return (
		<Modal>
			<section className={style.reviewSection}>
				<div className={style.header}>
					{reviews.length != 0 && (
						<div className={style.filterHolder}>
							<ReviewsFilter
								selectedReviewSort={selectedReviewSort}
								setSelectedReviewSort={setSelectedReviewSort}
							/>
						</div>
					)}
				</div>
				<div className={style.body}>
					<div className={style.main}>
						<ReviewsOverview reviews={reviews} course={course} />
						{mostLikedVideo?.like_count > 0 && (
							<MostLikedVideo
								course={course}
								video={mostLikedVideo}
							/>
						)}
					</div>
					<div className={style.reviewsHolder}>
						{reviews.length == 0 && (
							<EmptySection
								title={t("reviews:reviewSection.emptyTitle")}
								description={t("reviews:reviewSection.emptyDescription")}
							/>
						)}
						{sortedReviews.map((r) => (
							<Review key={r.uuid} review={r} course={course} />
						))}
					</div>
				</div>
			</section>

			<Modal.Window name="leaveReview" maxWidth="450px">
				<LeaveReviewForm course={course} />
			</Modal.Window>
		</Modal>
	);
}

export default ReviewSection;
